import { Component, OnInit, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, merge, Observable, of, filter, map, shareReplay, switchMap, take, tap, catchError, throwError } from 'rxjs';

import { TerraUtils } from 'src/app/shared/TerraUtils';

import { UnitApplicationParentType } from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import { UnitApplicationStatus } from 'src/app/shared/enums/unit-bank-account/UnitApplicationStatus.enum';
import { UnitDocumentReasonCode } from 'src/app/shared/enums/unit-bank-account/UnitDocumentReasonCode.enum';
import { UnitDocumentStatus } from 'src/app/shared/enums/unit-bank-account/UnitDocumentStatus.enum';
import { UnitDocumentType } from 'src/app/shared/enums/unit-bank-account/UnitDocumentType.enum';

import { DocumentFormModel } from 'src/app/shared/models/unit-bank-account/DocumentFormModel.model';
import { UnitBusinessApplicationReqRes } from 'src/app/shared/models/unit-bank-account/UnitBusinessApplicationReqRes.model';
import { UnitDocumentReqRes } from 'src/app/shared/models/unit-bank-account/UnitDocumentReqRes.model';
import { BaseResponseDto } from '../../../models/BaseResponseDto.model';
import { UnitDocumentConfirmDialogContext } from './unit-document-confirm-dialog/UnitDocumentConfirmDialogContext.model';

import { DialogService } from 'src/app/services/dialog.service';
import { UnitBankApplicationService } from 'src/app/services/shared/unit-bank-application.service';
import { RoutingService } from '../../../../services/routing.service';
import { UtilsService } from '../../../../services/utils.service';

import { ConfirmDialogParams } from '../../confirm-dialog/confirm-dialog.component';
import { AlertDialogParams } from '../../alert-dialog/alert-dialog.component';
import { FileExtension } from '../../file-upload/file-upload.component';
import { UnitDocumentConfirmDialogComponent } from './unit-document-confirm-dialog/unit-document-confirm-dialog.component';
import { PermissionService } from '../../../../permission/permission.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'terra-unit-application',
  templateUrl: './unit-application.component.html',
  styleUrls: ['./unit-application.component.scss'],
  providers: [UnitBankApplicationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitApplicationComponent implements OnInit {
  @HostBinding('class')
  className = '';
  // consts:

  ApplicationStatus = UnitApplicationStatus;
  UnitDocumentType = UnitDocumentType;
  UnitDocumentStatus = UnitDocumentStatus;
  UnitDocumentReasonCode = UnitDocumentReasonCode;

  documentSupportedExtensions = ['.pdf', '.jpeg', '.png', '.jpg'] as FileExtension[];

  application$ = this.unitBankApplicationService.application$.pipe(
    tap(app => this.className = app.parentType === UnitApplicationParentType.LP ? 'lp-host' : 'gp-host'),
    shareReplay(1));

  documents$ = this.application$.pipe(map(a => a.documents));
  aplicationStatus$ = this.application$.pipe(map((a) => a.status));

  allDocumentsUploaded$: Observable<boolean> = this.documents$.pipe(
    map(documents => this.getIsAllFilesUploaded(documents)));

  sendingDocuments$ = new BehaviorSubject(false);

  documentsForm: UntypedFormGroup;

  constructor(
    private router: Router,
    private routingService: RoutingService,
    private fb: UntypedFormBuilder,
    private unitBankApplicationService: UnitBankApplicationService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private utilsService: UtilsService,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.application$.pipe(
      take(1),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.router.navigateByUrl(this.routingService.bankAccountsPage("applications"));
        }
        return throwError(() => new Error("Something bad happened; please try again later."));
      })
    ).subscribe(application => {
      this.titleService.setTitle(`Bank Account Application: ${application.name} ${application.parentType === UnitApplicationParentType.LP ? TerraUtils.consts.LpPageTitleSuffix : TerraUtils.consts.GpPageTitleSuffix}`)
      this.generateDocumentsForm(application);
      this.handleFileChange();
      if (!this.getIsAllFilesUploaded(application.documents)) {
      }

    });
  }

  get isCovercyUser$() {
    return this.permissionService.isCovercyUser$;
  }

  refreshApplication() {
    this.unitBankApplicationService.refreshApplication();
  }

  getstatusIcon(status: UnitApplicationStatus) {
    switch (status) {
      case UnitApplicationStatus.Draft:
        return 'flip_to_back';
      case UnitApplicationStatus.AwaitingDocuments:
        return 'insert_drive_file';
      case UnitApplicationStatus.PendingReview:
        return 'more_horiz';
      case UnitApplicationStatus.Pending:
        return 'more_horiz';
      case UnitApplicationStatus.Approved:
        return 'check_box';
    }
  }

  getDocumentStatusIcon(status: UnitDocumentStatus) {
    switch (status) {
      case UnitDocumentStatus.Approved:
        return 'check';
      case UnitDocumentStatus.PendingReview:
      case UnitDocumentStatus.ReceivedFront:
      case UnitDocumentStatus.ReceivedBack:
        return 'pending';
      case UnitDocumentStatus.Invalid:
        return 'block';
    }
  }

  private getIsAllFilesUploaded(documents: UnitDocumentReqRes[]) {
    return documents.every((d) => d.metaFileLink && d.metaFileLink.id);
  }

  private generateDocumentsForm(application: UnitBusinessApplicationReqRes) {
    this.documentsForm = this.fb.group({
      documents: this.fb.array([])
    });

    if (application.documents && application.documents.length) {
      this.documentsForm.setControl('documents', this.fb.array(
        application.documents.map((d) => {
          return this.fb.group({
            documentId: d.id,
            metaFileLink: d.metaFileLink,
            backSideMetaFileLink: d.backSideMetaFileLink,
            isBackSideRequired: this.isDocument2Sided(d)
          } as DocumentFormModel);
        })
      ));
    }
  }

  private handleFileChange() {
    if (!this.documentsForm || (this.documentsForm.get("documents") as FormArray).controls?.length === 0) {
      return;
    }
    const documentsForms = (this.documentsForm.get("documents") as FormArray).controls;
    const documentFormsValueChanges = documentsForms.map(control => control.valueChanges as Observable<DocumentFormModel>);

    const userConfirmation = merge(...documentFormsValueChanges)
      .pipe(
        filter(documentForm => this.isAllDocumentFilesUploaded(documentForm)),
        switchMap(documentForm => combineLatest([this.confirmDocumentFileUpload(documentForm), of(documentForm)]).pipe(take(1))),
        shareReplay(1)
      );

    const confirmed$ = userConfirmation.pipe(
      filter(([isConfirmed]) => isConfirmed),
      switchMap(([, documentForm]) =>
        this.unitBankApplicationService.updateDocument(
          documentForm.documentId, documentForm.metaFileLink?.id, documentForm.backSideMetaFileLink?.id))
    );

    const rejected$ = userConfirmation.pipe(
      filter(([isConfirmed]) => !isConfirmed),
      tap(([, documentForm]) => {
        this.resetDocumentForm(documentForm.documentId);
      }
      ));

    merge(confirmed$, rejected$)
      .subscribe({
        next: () => {
          this.unitBankApplicationService.refreshApplication();
        },
        error: () => {
          var dialogParams = new AlertDialogParams();
          dialogParams.title = `An error has occurred`;
          dialogParams.description = `An error has occurred, please try again or contact support.`;
          dialogParams.actionLabel = `OK`;
          this.dialogService.alertDialog(dialogParams);
        }
      });
  }

  private isAllDocumentFilesUploaded(documentForm: DocumentFormModel): boolean {
    return coerceBooleanProperty(documentForm.metaFileLink && (!documentForm.isBackSideRequired || documentForm.backSideMetaFileLink));
  }

  private confirmDocumentFileUpload(documentForm: DocumentFormModel): Observable<boolean> {
    return this.getDocumentFromList(documentForm.documentId).pipe(
      switchMap(document => {
        const dialogParams = new MatDialogConfig<UnitDocumentConfirmDialogContext>();
        dialogParams.data = {
          documentName: document.name,
          documentType: document.documentType,
          metaFileLink: documentForm.metaFileLink,
          backSideMetaFileLink: documentForm.backSideMetaFileLink
        };
        return this.dialog.open(UnitDocumentConfirmDialogComponent, dialogParams).afterClosed();
      }),
      take(1));
  }

  private getDocumentFromList(documentId: number): Observable<UnitDocumentReqRes> {
    return this.documents$.pipe(map(documents => documents.find(d => d.id == documentId)));
  }

  private resetDocumentForm(documentId: number) {
    this.documents$.pipe(
      take(1),
      map(documents => documents.findIndex(d => d.id === documentId)),
    ).subscribe(indexOfDocument => {
      const documentsFormGroup = (this.documentsForm.get('documents') as FormArray).get(indexOfDocument.toString()) as UntypedFormGroup;
      documentsFormGroup.patchValue(
        ({
          ...documentsFormGroup.value,
          metaFileLink: null,
          backSideMetaFileLink: null
        } as DocumentFormModel),
        {
          emitEvent: false
        });
    });
  }

  private isDocument2Sided(document: UnitDocumentReqRes): boolean {
    return document.documentType === UnitDocumentType.IdDocument;
  }

  public splitByUppercase(str: string): string {
    if (!str) {
      return '';
    }

    const parts = str.match(/[A-Z][a-z]+/g);
    if (!parts) {
      return str;
    }

    return parts.join(' ');
  }

  deleteDraft(): void {
    this.application$.pipe(
      switchMap(application => {
        const dialogParams = new ConfirmDialogParams(`Delete Asset: ${application.name}`,
          `Any data associated with this application will be permanently deleted.<br>
        Are you sure you wish to delete the application?`);
        return this.dialogService.confirmDialog(dialogParams).afterClosed();
      }),
      filter(confirmed => !!confirmed),
      switchMap(() => this.unitBankApplicationService.deleteDraft())
    ).subscribe({
      next: () => {
        this.router.navigateByUrl(this.routingService.bankAccountsPage("applications"));
      },
      error: (error) => {
        if (error instanceof BaseResponseDto) {
          this.utilsService.alertErrorMessage(error);
        }
      }
    });
  }
}
